import { Box, SxProps, Theme, IconButton } from '@mui/material';
import EditCustomerConfirmation from 'customer-management/edit-customer-confirmation';
import { enqueueSnackbar } from 'notistack';
import { useRef, useState } from 'react';
import { UseTableCellProps } from 'react-table';
import { useCreateCustomer, useUpdateContract } from 'shared/mutations';
import { useUpdateDraftNameChange } from 'shared/mutations/update-name-change';
import { useEditCustomer } from 'shared/mutations/edit-customer';
import { useService } from 'shared/providers';
import { useAuthState } from 'shared/stores';
import { CustomerLookup, NameChangeReviewGridItem } from 'shared/types';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

const gridStyles: SxProps<Theme> = {
  display: 'flex',
};

export function NameChangeButtons(props: UseTableCellProps<NameChangeReviewGridItem>) {
  const {
    row: {
      original: { currentCustomerName, newCustomerName, brand, id, channel, customerTier, approvalStatus },
    },
  } = props;
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [customerAlreadyExists, setCustomerAlreadyExists] = useState(false);
  const [isRejectionModal, setIsRejectionModal] = useState(false);

  const resolveUserDecisionRef = useRef<((result: boolean) => void) | null>(null);
  const contractMutationUpdate = useUpdateContract();
  const customerMutationDelete = useEditCustomer();
  const customerMutationCreate = useCreateCustomer();
  const updateDraftMutation = useUpdateDraftNameChange();

  const authState = useAuthState();
  const user = authState.user.get();
  const { draftNameChangeService, customerService, contractService } = useService();

  const handleConfirmation = (): Promise<boolean> => {
    return new Promise(resolve => {
      setShowConfirmationModal(true);
      resolveUserDecisionRef.current = resolve;
    });
  };

  const updateDraftNameChange = async (isApproval: boolean) => {
    const nameChange = await draftNameChangeService.getDraftNameChange(id);
    if (nameChange) {
      if (isApproval) {
        nameChange.approvalStatus = 'Approved';
        nameChange.approvedBy = user?.displayName || '';
        nameChange.approvedDate = new Date().toISOString();
      } else {
        nameChange.approvalStatus = 'Rejected';
      }
      await updateDraftMutation.mutateAsync(nameChange);
    }
  };

  const handleApproval = async () => {
    const customers = await customerService.getCustomersLookupWithFilters({
      channel: '',
      customerTier: '',
      abgBrand: brand,
      masterCustomer: '',
    });
    const currentCustomerLookup = customers!.find(
      obj => obj['abgmastercustomer'] === currentCustomerName && obj['brand'] === brand
    );
    const contracts = await contractService.getContractsForCustomerAndBrand({
      customer: currentCustomerName,
      brand: brand,
    });
    setIsRejectionModal(false);

    if (customers!.some(obj => obj['abgmastercustomer'] === newCustomerName)) {
      setCustomerAlreadyExists(true);
      const userAgreed = await handleConfirmation();
      if (userAgreed) {
        if (contracts && contracts.length > 0) {
          contracts.forEach(async contract => {
            contract.customerName = newCustomerName;
            await contractMutationUpdate.mutateAsync(contract);
          });
        }
        await customerMutationDelete.mutateAsync(currentCustomerLookup!);
        await updateDraftNameChange(true);
      } else {
        enqueueSnackbar('Customer did not change!', { variant: 'success' });
      }
    } else {
      const userAgreed = await handleConfirmation();
      if (userAgreed) {
        const editedCustomer = createTempCustomerLookup(currentCustomerLookup!);
        editedCustomer.abgmastercustomer = newCustomerName;
        editedCustomer.channel = channel;
        editedCustomer.customertier = customerTier;
        editedCustomer.uniqueid = newCustomerName + editedCustomer.brand + editedCustomer.channel;
        await customerMutationCreate.mutateAsync(editedCustomer);
        if (contracts && contracts.length > 0) {
          contracts.forEach(async contract => {
            contract.customerName = editedCustomer.abgmastercustomer;
            await contractMutationUpdate.mutateAsync(contract);
          });
        }
        await customerMutationDelete.mutateAsync(currentCustomerLookup!);
        await updateDraftNameChange(true);
      }
    }
  };

  const handleRejection = async () => {
    setIsRejectionModal(true);
    const userAgreed = await handleConfirmation();
    if (userAgreed) {
      await updateDraftNameChange(false);
    }
  };

  const createTempCustomerLookup = (customer: CustomerLookup) => {
    const tempLookup = { ...customer };

    return tempLookup;
  };

  return (
    <Box sx={gridStyles}>
      {approvalStatus === 'Pending Approval' && (
        <>
          <IconButton
            sx={{
              color: 'grey',
              '&:hover': {
                color: '#2E7D32',
              },
            }}
            onClick={handleApproval}
          >
            <CheckIcon />
          </IconButton>
          <IconButton
            sx={{
              color: 'grey',
              '&:hover': {
                color: '#D32F2F',
              },
            }}
            onClick={handleRejection}
          >
            <CloseIcon />
          </IconButton>
        </>
      )}
      {showConfirmationModal && (
        <EditCustomerConfirmation
          isOpen={showConfirmationModal}
          onDialogDecision={result => {
            if (resolveUserDecisionRef.current) {
              resolveUserDecisionRef.current(result);
            }
            setShowConfirmationModal(false);
          }}
          currentCustomer={currentCustomerName}
          newAbgMasterName={newCustomerName}
          brand={brand}
          customerAlreadyExists={customerAlreadyExists}
          isRejection={isRejectionModal}
        />
      )}
    </Box>
  );
}
